import React from 'react'
import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import AmbuNetLogo from '../../../assets/images/ambunetLogo.png'
import {
  ACTION_STATUS_COLOR,
  CLASSIFICATION_COLOR,
  HARM_COLOR,
  PRIORITY_COLOR,
  REPORTING_BODIES,
  REPORTING_STATE_COLOR,
  RISK_COLOR,
  STATUS_COLOR,
  isOpen,
  slaState,
} from './constants'

// Landscape A4 export of the Safety Incident Management Log, in the same
// chrome as the hazard-log PDF: a cover page with the download stamp and
// headline numbers, a register table (one row per incident), then a detail
// block per incident that walks the DCB0129 record from report to CSO
// sign-off. Colours match the CRM's chips so the PDF reads the same as the
// screen. Pass a single incident (with its timeline) for a one-incident
// report from the detail page.

const NAVY = '#123F4A'
const TEAL = '#239AA4'
const MUTED = '#64748B'
const LINE = '#E2E8F0'
const INK = '#334155'

// Helvetica (the built-in PDF font) only covers Latin-1, so swap the symbols
// free text tends to contain for ASCII and drop anything else rather than
// print tofu.
const clean = (value) =>
  String(value ?? '')
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=')
    .replace(/≠/g, '!=')
    .replace(/[—–]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/•/g, '-')
    .replace(/[^\x00-\xFF]/g, '')

const fullName = (user) =>
  user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : ''

const fmtDate = (value) => (value ? moment(value).format('D MMM YYYY') : '')
const fmtDateTime = (value) =>
  value ? moment(value).format('D MMM YYYY HH:mm') : ''

const idNumber = (id = '') => Number(String(id).replace(/\D/g, '')) || 0

const effectiveHarm = (incident) =>
  incident.triage?.actualHarm && incident.triage.actualHarm !== 'Unknown'
    ? incident.triage.actualHarm
    : incident.reporterHarm

const countBy = (rows, getKey, keys) => {
  const counts = Object.fromEntries(keys.map((key) => [key, 0]))
  let other = 0
  rows.forEach((row) => {
    const key = getKey(row)
    if (key in counts) counts[key] += 1
    else other += 1
  })
  return { counts, other }
}

// ---------------------------------------------------------------------------
// Building blocks (shared look with HazardLogPDF)
// ---------------------------------------------------------------------------

const Pill = ({ label, color }) => (
  <View
    style={{
      backgroundColor: color || '#F1F5F9',
      borderRadius: 3,
      paddingVertical: 1.5,
      paddingHorizontal: 4,
      alignSelf: 'flex-start',
    }}
  >
    <Text
      style={{
        fontSize: 6.5,
        color: color ? '#FFFFFF' : MUTED,
        fontFamily: 'Helvetica-Bold',
      }}
    >
      {clean(label)}
    </Text>
  </View>
)

const SectionTitle = ({ children, marginTop = 14 }) => (
  <Text
    style={{
      fontSize: 11,
      color: NAVY,
      fontFamily: 'Helvetica-Bold',
      marginTop,
      marginBottom: 4,
    }}
    minPresenceAhead={40}
  >
    {children}
  </Text>
)

const StatBox = ({ label, value, color, small }) => (
  <View
    style={{
      flex: 1,
      borderWidth: 0.5,
      borderColor: LINE,
      borderRadius: 4,
      padding: 8,
      marginRight: 6,
    }}
  >
    <Text
      style={{
        fontSize: 6.5,
        color: MUTED,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Text>
    <Text
      style={{
        fontSize: 18,
        color: color || NAVY,
        fontFamily: 'Helvetica-Bold',
        marginTop: 2,
        ...(small ? { fontWeight: 100, fontSize: 8 } : {}),
      }}
    >
      {value}
    </Text>
  </View>
)

const CountTable = ({ title, rows }) => (
  <View style={{ flex: 1, marginRight: 8 }}>
    <Text
      style={{
        fontSize: 8,
        color: NAVY,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 3,
      }}
    >
      {title}
    </Text>
    {rows.map(([label, value, color]) => (
      <View
        key={label}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 2,
          borderBottomWidth: 0.5,
          borderBottomColor: LINE,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {color ? (
            <View
              style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                backgroundColor: color,
                marginRight: 4,
              }}
            />
          ) : null}
          <Text style={{ fontSize: 7.5 }}>{label}</Text>
        </View>
        <Text style={{ fontSize: 7.5, fontFamily: 'Helvetica-Bold' }}>
          {value}
        </Text>
      </View>
    ))}
  </View>
)

const Label = ({ children }) => (
  <Text
    style={{
      fontSize: 6,
      color: MUTED,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginTop: 5,
      marginBottom: 1,
      fontFamily: 'Helvetica-Bold',
    }}
  >
    {children}
  </Text>
)

const Body = ({ children, muted }) => (
  <Text style={{ fontSize: 6.5, color: muted ? MUTED : INK, lineHeight: 1.3 }}>
    {children}
  </Text>
)

const Field = ({ label, value }) =>
  value ? (
    <View style={{ marginBottom: 2 }}>
      <Label>{label}</Label>
      <Body>{clean(value)}</Body>
    </View>
  ) : null

const Footer = () => (
  <View
    style={{
      position: 'absolute',
      bottom: 16,
      left: 30,
      right: 30,
      flexDirection: 'row',
      justifyContent: 'space-between',
    }}
    fixed
  >
    <Text style={{ fontSize: 6.5, color: '#94A3B8' }}>
      AmbuNet - Safety Incident Management Log - DCB0129 - CONFIDENTIAL
    </Text>
    <Text
      style={{ fontSize: 6.5, color: '#94A3B8' }}
      render={({ pageNumber, totalPages }) =>
        `Page ${pageNumber} of ${totalPages}`
      }
    />
  </View>
)

const PAGE_STYLE = {
  paddingTop: 30,
  paddingBottom: 36,
  paddingHorizontal: 30,
  fontFamily: 'Helvetica',
  fontSize: 8,
  color: '#0F172A',
}

// ---------------------------------------------------------------------------
// Register table
// ---------------------------------------------------------------------------

const COLUMNS = [
  ['ID', 0.8],
  ['Incident', 3.2],
  ['Tenant / Module', 1.5],
  ['Status', 1.1],
  ['Classification', 1.1],
  ['Harm', 1],
  ['Risk', 0.9],
  ['Targets', 1.1],
  ['Hazards', 0.9],
  ['Reported', 0.9],
]

const TableHeader = () => (
  <View
    style={{
      flexDirection: 'row',
      backgroundColor: NAVY,
      paddingVertical: 4,
      paddingHorizontal: 4,
      borderRadius: 3,
    }}
    fixed
  >
    {COLUMNS.map(([label, flex]) => (
      <Text
        key={label}
        style={{
          flex,
          paddingHorizontal: 3,
          fontSize: 6.5,
          color: '#FFFFFF',
          fontFamily: 'Helvetica-Bold',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          textAlign: 'left',
        }}
      >
        {label}
      </Text>
    ))}
  </View>
)

const targetSummary = (incident) => {
  const sla = slaState(incident)
  const parts = []
  if (sla.acknowledge) {
    parts.push(
      sla.acknowledge.key === 'met'
        ? sla.acknowledge.late
          ? 'Ack late'
          : 'Ack on time'
        : sla.acknowledge.key === 'overdue'
          ? 'Ack overdue'
          : 'Ack due',
    )
  }
  if (sla.classify) {
    parts.push(
      sla.classify.key === 'met'
        ? sla.classify.late
          ? 'Classified late'
          : 'Classified on time'
        : sla.classify.key === 'overdue'
          ? 'Classify overdue'
          : 'Classify due',
    )
  }
  const bad = parts.some((p) => /late|overdue/.test(p))
  return {
    text: parts.join(', ') || '-',
    color: bad ? '#B42318' : parts.length ? '#27803B' : MUTED,
  }
}

const RegisterRow = ({ incident, striped }) => {
  const harm = effectiveHarm(incident)
  const rating = incident.triage?.riskRating
  const targets = targetSummary(incident)
  const linked = incident.hazards?.linkedHazardIDs || []
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 4,
        backgroundColor: striped ? '#F8FAFC' : undefined,
        borderBottomWidth: 0.5,
        borderBottomColor: LINE,
      }}
      wrap={false}
    >
      <Text
        style={{
          flex: 0.8,
          paddingHorizontal: 3,
          fontSize: 7,
          fontFamily: 'Helvetica-Bold',
        }}
      >
        {incident.incidentID}
      </Text>
      <View style={{ flex: 3.2, paddingHorizontal: 3 }}>
        <Text style={{ fontSize: 7 }}>{clean(incident.title)}</Text>
        {incident.triage?.priority ? (
          <Text
            style={{
              fontSize: 6,
              color: PRIORITY_COLOR[incident.triage.priority] || MUTED,
            }}
          >
            {incident.triage.priority}
          </Text>
        ) : null}
      </View>
      <View style={{ flex: 1.5, paddingHorizontal: 3 }}>
        <Text style={{ fontSize: 6.5 }}>
          {clean(
            incident.tenant?.name ||
              incident.tenant?.subdomain ||
              'Unknown tenant',
          )}
        </Text>
        <Text style={{ fontSize: 6, color: MUTED }}>
          {[
            incident.module,
            incident.platform !== 'Unknown' ? incident.platform : null,
          ]
            .filter(Boolean)
            .map(clean)
            .join(' / ')}
        </Text>
      </View>
      <View style={{ flex: 1.1, paddingHorizontal: 3 }}>
        <Pill
          label={incident.status || 'Unknown'}
          color={STATUS_COLOR[incident.status]}
        />
      </View>
      <View style={{ flex: 1.1, paddingHorizontal: 3 }}>
        <Pill
          label={incident.classification || 'Unclassified'}
          color={CLASSIFICATION_COLOR[incident.classification]}
        />
      </View>
      <View style={{ flex: 1, paddingHorizontal: 3 }}>
        <Pill label={harm || 'Unknown'} color={HARM_COLOR[harm]} />
      </View>
      <View style={{ flex: 0.9, paddingHorizontal: 3, alignItems: 'center' }}>
        <Pill label={rating || 'Not scored'} color={RISK_COLOR[rating]} />
      </View>
      <Text
        style={{
          flex: 1.1,
          paddingHorizontal: 3,
          fontSize: 6,
          color: targets.color,
        }}
      >
        {targets.text}
      </Text>
      <Text
        style={{
          flex: 0.9,
          paddingHorizontal: 3,
          fontSize: 6.5,
          textAlign: 'center',
        }}
      >
        {linked.length ? linked.join(', ') : '-'}
      </Text>
      <Text
        style={{ flex: 0.9, paddingHorizontal: 3, fontSize: 6.5, color: MUTED }}
      >
        {fmtDate(incident.reportedAt) || '-'}
      </Text>
    </View>
  )
}

// ---------------------------------------------------------------------------
// Detail block
// ---------------------------------------------------------------------------

const Column = ({ children, flex = 1, first }) => (
  <View
    style={{
      flex,
      paddingLeft: first ? 0 : 10,
      paddingRight: 10,
      borderLeftWidth: first ? 0 : 0.5,
      borderLeftColor: LINE,
    }}
  >
    {children}
  </View>
)

const Dot = ({ color }) => (
  <View
    style={{
      width: 6,
      height: 6,
      borderRadius: 3,
      marginTop: 1.5,
      marginRight: 4,
      backgroundColor: color || '#94A3B8',
    }}
  />
)

const IncidentDetail = ({ incident, showTimeline }) => {
  const t = incident.triage || {}
  const inv = incident.investigation || {}
  const ext = incident.externalReporting || {}
  const comms = incident.customerCommunication || {}
  const closure = incident.closure || {}
  const signOff = closure.csoSignOff || {}
  const actions = incident.actions || []
  const harm = effectiveHarm(incident)
  const sla = slaState(incident)

  return (
    <View
      style={{
        borderWidth: 0.5,
        borderColor: LINE,
        borderRadius: 4,
        marginBottom: 8,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: '#F1F5F9',
          paddingVertical: 4,
          paddingHorizontal: 8,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
        wrap={false}
        minPresenceAhead={80}
      >
        <Text
          style={{
            fontSize: 8,
            fontFamily: 'Helvetica-Bold',
            color: NAVY,
            marginRight: 8,
          }}
        >
          {incident.incidentID}
        </Text>
        <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', flex: 1 }}>
          {clean(incident.title)}
        </Text>
        <View style={{ marginLeft: 6 }}>
          <Pill
            label={incident.status || 'Unknown'}
            color={STATUS_COLOR[incident.status]}
          />
        </View>
        <View style={{ marginLeft: 6 }}>
          <Pill
            label={incident.classification || 'Unclassified'}
            color={CLASSIFICATION_COLOR[incident.classification]}
          />
        </View>
        <View style={{ marginLeft: 6 }}>
          <Pill label={`Harm: ${harm || 'Unknown'}`} color={HARM_COLOR[harm]} />
        </View>
        <View style={{ marginLeft: 6 }}>
          <Pill
            label={`Risk: ${t.riskRating || 'Not scored'}`}
            color={RISK_COLOR[t.riskRating]}
          />
        </View>
      </View>

      <View
        style={{
          flexDirection: 'row',
          paddingHorizontal: 8,
          paddingVertical: 6,
        }}
      >
        {/* What was reported */}
        <Column flex={1.4} first>
          <Text style={{ fontSize: 6.5, color: MUTED, marginBottom: 3 }}>
            {[
              incident.tenant?.name || incident.tenant?.subdomain,
              incident.region,
              incident.module,
              incident.platform !== 'Unknown' ? incident.platform : null,
              incident.softwareVersion ? `v${incident.softwareVersion}` : null,
              `Source: ${incident.source || 'Unknown'}`,
              `Reported ${fmtDateTime(incident.reportedAt)}`,
              incident.occurredAt
                ? `Occurred ${fmtDateTime(incident.occurredAt)}`
                : null,
            ]
              .filter(Boolean)
              .map(clean)
              .join('  |  ')}
          </Text>
          <Text style={{ fontSize: 7, lineHeight: 1.35 }}>
            {clean(incident.description)}
          </Text>
          <Field
            label="Immediate actions taken"
            value={incident.immediateActions}
          />
          <Label>Reporter</Label>
          <Body>
            {clean(
              [
                incident.reporter?.name || 'Unknown',
                incident.reporter?.email,
                incident.reporter?.phone,
                `harm assessed as ${incident.reporterHarm || 'Unknown'}`,
                incident.isOngoing === 'Yes'
                  ? 'still happening at report'
                  : null,
                incident.patientsAffected
                  ? `${incident.patientsAffected} patient(s) affected`
                  : null,
              ]
                .filter(Boolean)
                .join(' - '),
            )}
          </Body>
          {incident.recordReferences?.length ? (
            <Field
              label="Record references"
              value={incident.recordReferences.join(', ')}
            />
          ) : null}
          {incident.clickUp?.ticketRef ? (
            <Field label="Support ticket" value={incident.clickUp.ticketRef} />
          ) : null}

          <Label>Investigation</Label>
          {inv.summary || inv.rootCause ? (
            <>
              {inv.summary ? <Body>{clean(inv.summary)}</Body> : null}
              {inv.rootCause ? (
                <Body>
                  <Text style={{ fontFamily: 'Helvetica-Bold' }}>
                    Root cause:{' '}
                  </Text>
                  {clean(inv.rootCause)}
                </Body>
              ) : null}
            </>
          ) : (
            <Body muted>Not yet recorded</Body>
          )}
          <Body muted>
            {clean(
              [
                inv.contributingFactors?.length
                  ? `Factors: ${inv.contributingFactors.join(', ')}`
                  : null,
                inv.affectedVersions?.length
                  ? `Versions: ${inv.affectedVersions.join(', ')}`
                  : null,
                inv.otherTenantsAffected &&
                inv.otherTenantsAffected !== 'Unknown'
                  ? `Other tenants affected: ${inv.otherTenantsAffected}${inv.affectedTenants?.length ? ` (${inv.affectedTenants.join(', ')})` : ''}`
                  : null,
                fullName(inv.lead) ? `Lead: ${fullName(inv.lead)}` : null,
              ]
                .filter(Boolean)
                .join('   '),
            )}
          </Body>
        </Column>

        {/* Triage, hazards, actions */}
        <Column flex={1.1}>
          <Label>Triage</Label>
          <Body>
            {clean(
              [
                t.priority || 'No priority',
                `actual ${t.actualHarm || 'Unknown'}`,
                `potential ${t.potentialHarm || 'Unknown'}`,
                t.likelihood && t.severity
                  ? `${t.likelihood} / ${t.severity} = ${t.riskRating}`
                  : 'not scored',
              ].join('  |  '),
            )}
          </Body>
          <Body muted>
            {clean(
              [
                sla.acknowledge
                  ? sla.acknowledge.key === 'met'
                    ? `Acknowledged ${fmtDateTime(t.acknowledgedAt)}${fullName(t.acknowledgedBy) ? ` by ${fullName(t.acknowledgedBy)}` : ''}${sla.acknowledge.late ? ' (late)' : ''}`
                    : `Acknowledgement ${sla.acknowledge.key}`
                  : null,
                t.triagedAt ? `Triaged ${fmtDateTime(t.triagedAt)}` : null,
              ]
                .filter(Boolean)
                .join('. '),
            )}
          </Body>
          {t.notes ? <Body>{clean(t.notes)}</Body> : null}

          <Label>Hazard log</Label>
          <Body>
            {incident.hazards?.linkedHazardIDs?.length
              ? `Linked: ${incident.hazards.linkedHazardIDs.join(', ')}`
              : incident.hazards?.newHazardRequired === 'No'
                ? 'No hazard required'
                : 'Not yet linked'}
          </Body>
          {incident.hazards?.notes ? (
            <Body muted>{clean(incident.hazards.notes)}</Body>
          ) : null}

          <Label>
            Actions ({actions.filter((a) => a.status === 'Done').length} of{' '}
            {actions.length} done)
          </Label>
          {actions.length ? (
            actions.map((action, index) => (
              <View
                key={action._id || index}
                style={{ flexDirection: 'row', marginBottom: 2 }}
              >
                <Dot color={ACTION_STATUS_COLOR[action.status]} />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 6.5 }}>
                    <Text style={{ fontFamily: 'Helvetica-Bold' }}>
                      {action.type}:{' '}
                    </Text>
                    {clean(action.description)}
                    <Text style={{ color: MUTED }}>
                      {' '}
                      ({action.status}
                      {action.dueDate ? `, due ${fmtDate(action.dueDate)}` : ''}
                      {fullName(action.owner)
                        ? `, ${fullName(action.owner)}`
                        : ''}
                      )
                    </Text>
                  </Text>
                  {action.evidence ? (
                    <Body muted>Evidence: {clean(action.evidence)}</Body>
                  ) : null}
                </View>
              </View>
            ))
          ) : (
            <Body muted>None recorded</Body>
          )}
        </Column>

        {/* Reporting, comms, safety case, closure */}
        <Column flex={1}>
          <Label>External reporting</Label>
          {REPORTING_BODIES.map(({ key, label }) => {
            const entry = ext[key]
            const state = entry?.state || 'Not assessed'
            return (
              <View
                key={key}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: 1.5,
                }}
              >
                <Dot color={REPORTING_STATE_COLOR[state]} />
                <Text style={{ fontSize: 6.5, flex: 1 }}>
                  {label}: <Text style={{ color: MUTED }}>{state}</Text>
                  {entry?.reference ? ` ${clean(entry.reference)}` : ''}
                  {entry?.date ? ` ${fmtDate(entry.date)}` : ''}
                </Text>
              </View>
            )
          })}
          {ext.dutyOfCandour?.applies &&
          ext.dutyOfCandour.applies !== 'Unknown' ? (
            <Body muted>
              Duty of candour: {ext.dutyOfCandour.applies}
              {ext.dutyOfCandour.notes
                ? ` - ${clean(ext.dutyOfCandour.notes)}`
                : ''}
            </Body>
          ) : null}

          <Label>Customer communication</Label>
          {comms.notifiedAt ||
          comms.safetyNoticeIssued ||
          comms.workaroundProvided ? (
            <Body>
              {clean(
                [
                  comms.notifiedAt
                    ? `Notified ${fmtDateTime(comms.notifiedAt)}${comms.method ? ` by ${comms.method}` : ''}`
                    : null,
                  comms.safetyNoticeIssued
                    ? `Safety notice issued${comms.safetyNoticeRef ? ` (${comms.safetyNoticeRef})` : ''}`
                    : null,
                  comms.workaroundProvided
                    ? `Workaround: ${comms.workaroundProvided}`
                    : null,
                  comms.releaseNotesRef
                    ? `Release notes: ${comms.releaseNotesRef}`
                    : null,
                ]
                  .filter(Boolean)
                  .join('. '),
              )}
            </Body>
          ) : (
            <Body muted>Not yet recorded</Body>
          )}

          <Label>Safety case</Label>
          <Body>
            {incident.safetyCase?.impact || 'Not assessed'}
            {incident.safetyCase?.cscrUpdatedAt
              ? ` (CSCR updated ${fmtDate(incident.safetyCase.cscrUpdatedAt)})`
              : ''}
          </Body>
          {incident.safetyCase?.notes ? (
            <Body muted>{clean(incident.safetyCase.notes)}</Body>
          ) : null}

          <Label>Closure</Label>
          {closure.closedAt ? (
            <>
              <Body>
                Closed {fmtDateTime(closure.closedAt)}
                {fullName(closure.closedBy)
                  ? ` by ${fullName(closure.closedBy)}`
                  : ''}
              </Body>
              {closure.summary ? <Body>{clean(closure.summary)}</Body> : null}
              {closure.lessonsLearned ? (
                <Body>
                  <Text style={{ fontFamily: 'Helvetica-Bold' }}>
                    Lessons learned:{' '}
                  </Text>
                  {clean(closure.lessonsLearned)}
                </Body>
              ) : null}
            </>
          ) : (
            <Body muted>Open</Body>
          )}
          <Body>
            <Text style={{ fontFamily: 'Helvetica-Bold' }}>CSO sign-off: </Text>
            {signOff.signedOff
              ? `yes${signOff.signedOffDate ? ` (${fmtDate(signOff.signedOffDate)}${fullName(signOff.signedOffBy) ? `, ${fullName(signOff.signedOffBy)}` : ''})` : ''}`
              : 'no'}
          </Body>
        </Column>
      </View>

      {showTimeline && incident.timeline?.length ? (
        <View
          style={{
            paddingHorizontal: 8,
            paddingBottom: 6,
            borderTopWidth: 0.5,
            borderTopColor: LINE,
          }}
        >
          <Label>Timeline</Label>
          {[...incident.timeline]
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map((entry, index) => (
              <View
                key={entry._id || index}
                style={{ flexDirection: 'row', marginBottom: 1.5 }}
              >
                <Text style={{ fontSize: 6, color: MUTED, width: 78 }}>
                  {fmtDateTime(entry.date)}
                </Text>
                <Text style={{ fontSize: 6, color: MUTED, width: 90 }}>
                  {clean(fullName(entry.by) || entry.byName || 'System')}
                </Text>
                <Text style={{ fontSize: 6.5, flex: 1 }}>
                  {entry.status ? (
                    <Text style={{ color: MUTED }}>[{entry.status}] </Text>
                  ) : null}
                  {clean(entry.comment)}
                </Text>
              </View>
            ))}
        </View>
      ) : null}
    </View>
  )
}

// ---------------------------------------------------------------------------
// Document
// ---------------------------------------------------------------------------

const SafetyIncidentLogPDF = ({
  incidents = [],
  downloadedBy,
  single = false,
}) => {
  const now = moment()
  const sorted = [...incidents].sort(
    (a, b) => idNumber(a.incidentID) - idNumber(b.incidentID),
  )
  const title =
    single && sorted[0]
      ? `Safety incident ${sorted[0].incidentID}`
      : 'Safety Incident Management Log'

  const status = countBy(sorted, (i) => i.status, [
    'Reported',
    'Acknowledged',
    'Under Investigation',
    'Actions In Progress',
    'Awaiting Verification',
    'Closed',
  ])
  const classification = countBy(sorted, (i) => i.classification, [
    'Safety Incident',
    'Near Miss',
    'Safety Concern',
    'Not a Safety Incident',
  ])
  const harm = countBy(sorted, effectiveHarm, [
    'Death',
    'Severe harm',
    'Moderate harm',
    'Low harm',
    'No harm',
    'Near miss',
  ])
  const risk = countBy(sorted, (i) => i.triage?.riskRating, [
    'Critical',
    'High',
    'Medium',
    'Low',
  ])
  const impact = countBy(sorted, (i) => i.safetyCase?.impact, [
    'CSCR update required',
    'CSCR updated',
    'Hazard log updated',
    'No impact',
  ])

  const open = sorted.filter(isOpen)
  const unacknowledged = open.filter((i) => !i.triage?.acknowledgedAt).length
  const lateTargets = sorted.filter((i) => {
    const sla = slaState(i)
    return (
      sla.acknowledge?.late ||
      sla.acknowledge?.key === 'overdue' ||
      sla.classify?.late ||
      sla.classify?.key === 'overdue'
    )
  }).length
  const serious = sorted.filter((i) =>
    ['Moderate harm', 'Severe harm', 'Death'].includes(effectiveHarm(i)),
  ).length
  const signedOff = sorted.filter(
    (i) => i.closure?.csoSignOff?.signedOff,
  ).length
  const unlinked = sorted.filter(
    (i) =>
      ['Safety Incident', 'Near Miss', 'Safety Concern'].includes(
        i.classification,
      ) && !(i.hazards?.linkedHazardIDs || []).length,
  ).length

  const numbers = sorted.map((i) => idNumber(i.incidentID)).filter(Boolean)
  const idRange = numbers.length
    ? `${sorted.find((i) => idNumber(i.incidentID) === Math.min(...numbers))?.incidentID} to ${sorted.find((i) => idNumber(i.incidentID) === Math.max(...numbers))?.incidentID}`
    : 'None'

  const byTenant = {}
  sorted.forEach((i) => {
    const key = i.tenant?.name || i.tenant?.subdomain || 'Unknown tenant'
    byTenant[key] = (byTenant[key] || 0) + 1
  })
  const tenantRows = Object.entries(byTenant)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)

  return (
    <Document
      title={`${title} - ${now.format('YYYY-MM-DD')}`}
      author="AmbuNet"
      subject="DCB0129 safety incident management log export"
      options={{ isEvalSupported: false }}
    >
      {/* Cover */}
      <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Image
              src={AmbuNetLogo}
              style={{ width: 44, height: 44, marginRight: 10 }}
            />
            <View>
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: 'Helvetica-Bold',
                  color: NAVY,
                }}
              >
                AmbuNet
              </Text>
              <Text
                style={{
                  fontSize: 7,
                  color: MUTED,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                }}
              >
                Clinical safety
              </Text>
            </View>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 7, color: MUTED }}>
              Downloaded {now.format('dddd D MMMM YYYY, HH:mm')}
            </Text>
            {downloadedBy ? (
              <Text style={{ fontSize: 7, color: MUTED }}>
                by {clean(downloadedBy)}
              </Text>
            ) : null}
          </View>
        </View>

        <View
          style={{
            marginTop: 22,
            paddingVertical: 10,
            paddingHorizontal: 10,
            borderRadius: 6,
            backgroundColor: NAVY,
          }}
        >
          <Text
            style={{
              fontSize: 26,
              color: '#fff',
              fontFamily: 'Helvetica-Bold',
            }}
          >
            {title}
          </Text>
          <View
            style={{
              marginTop: 10,
              paddingVertical: 10,
              paddingHorizontal: 6,
              borderRadius: 6,
              backgroundColor: '#fff',
            }}
          >
            <Text style={{ fontSize: 9, color: NAVY, marginTop: 4 }}>
              DCB0129 Clinical Risk Management: its Application in the
              Manufacture of Health IT Systems - Safety Incident Management Log
              (sections 3.6.1 and 7.2)
            </Text>
            <Text
              style={{
                fontSize: 7.5,
                color: NAVY,
                marginTop: 6,
                lineHeight: 1.4,
                maxWidth: 640,
              }}
            >
              Every safety concern and incident reported against AmbuNet, from
              first report through CSO triage, investigation, hazard-log
              linkage, corrective action, external reporting and customer
              communication to signed closure. Entries are never deleted; a
              report found not to be a safety incident is classified as such and
              closed.
            </Text>
          </View>
        </View>

        <SectionTitle>Summary as at {now.format('D MMMM YYYY')}</SectionTitle>
        <View style={{ flexDirection: 'row' }}>
          <StatBox label="Incidents logged" value={sorted.length} />
          <StatBox label="ID range" value={idRange} small />
          <StatBox
            label="Open"
            value={open.length}
            color={open.length ? '#B54708' : '#27803B'}
          />
          <StatBox
            label="Unacknowledged"
            value={unacknowledged}
            color={unacknowledged ? '#B42318' : '#27803B'}
          />
          <StatBox
            label="Targets missed"
            value={lateTargets}
            color={lateTargets ? '#B42318' : '#27803B'}
          />
          <StatBox
            label="Moderate+ Harm"
            value={serious}
            color={serious ? '#7A271A' : '#27803B'}
          />
          <StatBox
            label="CSO signed off"
            value={`${signedOff} of ${sorted.length}`}
            color={TEAL}
          />
        </View>

        <View style={{ flexDirection: 'row', marginTop: 14 }}>
          <CountTable
            title="By status"
            rows={[
              ['Reported', status.counts.Reported, STATUS_COLOR.Reported],
              [
                'Acknowledged',
                status.counts.Acknowledged,
                STATUS_COLOR.Acknowledged,
              ],
              [
                'Under Investigation',
                status.counts['Under Investigation'],
                STATUS_COLOR['Under Investigation'],
              ],
              [
                'Actions In Progress',
                status.counts['Actions In Progress'],
                STATUS_COLOR['Actions In Progress'],
              ],
              [
                'Awaiting Verification',
                status.counts['Awaiting Verification'],
                STATUS_COLOR['Awaiting Verification'],
              ],
              ['Closed', status.counts.Closed, STATUS_COLOR.Closed],
            ]}
          />
          <CountTable
            title="By classification"
            rows={[
              [
                'Safety Incident',
                classification.counts['Safety Incident'],
                CLASSIFICATION_COLOR['Safety Incident'],
              ],
              [
                'Near Miss',
                classification.counts['Near Miss'],
                CLASSIFICATION_COLOR['Near Miss'],
              ],
              [
                'Safety Concern',
                classification.counts['Safety Concern'],
                CLASSIFICATION_COLOR['Safety Concern'],
              ],
              [
                'Not a Safety Incident',
                classification.counts['Not a Safety Incident'],
                CLASSIFICATION_COLOR['Not a Safety Incident'],
              ],
              [
                'Unclassified',
                classification.other,
                CLASSIFICATION_COLOR.Unclassified,
              ],
            ]}
          />
          <CountTable
            title="By harm (actual, else reported)"
            rows={[
              ['Death', harm.counts.Death, HARM_COLOR.Death],
              [
                'Severe harm',
                harm.counts['Severe harm'],
                HARM_COLOR['Severe harm'],
              ],
              [
                'Moderate harm',
                harm.counts['Moderate harm'],
                HARM_COLOR['Moderate harm'],
              ],
              ['Low harm', harm.counts['Low harm'], HARM_COLOR['Low harm']],
              ['No harm', harm.counts['No harm'], HARM_COLOR['No harm']],
              ['Near miss', harm.counts['Near miss'], HARM_COLOR['Near miss']],
              ['Unknown', harm.other],
            ]}
          />
          <CountTable
            title="By triage risk"
            rows={[
              ['Critical', risk.counts.Critical, RISK_COLOR.Critical],
              ['High', risk.counts.High, RISK_COLOR.High],
              ['Medium', risk.counts.Medium, RISK_COLOR.Medium],
              ['Low', risk.counts.Low, RISK_COLOR.Low],
              ['Not scored', risk.other],
            ]}
          />
          <CountTable
            title="Safety case impact"
            rows={[
              [
                'CSCR update required',
                impact.counts['CSCR update required'],
                '#B42318',
              ],
              ['CSCR updated', impact.counts['CSCR updated'], '#268EBB'],
              [
                'Hazard log updated',
                impact.counts['Hazard log updated'],
                '#5B48B0',
              ],
              ['No impact', impact.counts['No impact'], '#27803B'],
              ['Not assessed', impact.other],
              ['Awaiting hazard link', unlinked, '#B54708'],
            ]}
          />
          <CountTable
            title="By tenant"
            rows={tenantRows.length ? tenantRows : [['None', 0]]}
          />
        </View>

        <View style={{ marginTop: 14 }}>
          <Text
            style={{
              fontSize: 8,
              color: NAVY,
              fontFamily: 'Helvetica-Bold',
              marginBottom: 3,
            }}
          >
            Targets (CRMP section 8.6)
          </Text>
          <Text style={{ fontSize: 6.5, color: MUTED, lineHeight: 1.4 }}>
            Acknowledge within one working day of the report. Classify within
            two working days as Safety Incident, Near Miss, Safety Concern or
            Not a Safety Incident. Link to or raise a hazard within two working
            days. Investigate in proportion to the triage rating: same day for
            Critical, within the week for High, next release review for Medium
            and Low. Every incident is assessed for its impact on the safety
            case before closure, and closure is signed off by the Clinical
            Safety Officer.
          </Text>
        </View>

        <Footer />
      </Page>

      {/* Register table */}
      {!single && (
        <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
            fixed
          >
            <Text
              style={{
                fontSize: 11,
                fontFamily: 'Helvetica-Bold',
                color: NAVY,
              }}
            >
              Safety Incident Management Log
            </Text>
            <Text style={{ fontSize: 7, color: MUTED }}>
              {sorted.length} incidents, downloaded{' '}
              {now.format('D MMM YYYY HH:mm')}
            </Text>
          </View>
          <TableHeader />
          {sorted.length === 0 ? (
            <Text style={{ fontSize: 8, color: MUTED, padding: 8 }}>
              No incidents have been logged.
            </Text>
          ) : (
            sorted.map((incident, index) => (
              <RegisterRow
                key={incident._id || incident.incidentID}
                incident={incident}
                striped={index % 2 === 1}
              />
            ))
          )}
          <Footer />
        </Page>
      )}

      {/* Detail */}
      {sorted.length > 0 && (
        <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
            fixed
          >
            <Text
              style={{
                fontSize: 11,
                fontFamily: 'Helvetica-Bold',
                color: NAVY,
              }}
            >
              {single
                ? `${title} - full record`
                : 'Safety Incident Management Log - incident detail'}
            </Text>
            <Text style={{ fontSize: 7, color: MUTED }}>
              Report, triage, investigation, hazard linkage, actions, reporting,
              communication and closure per incident
            </Text>
          </View>
          {sorted.map((incident) => (
            <IncidentDetail
              key={incident._id || incident.incidentID}
              incident={incident}
              showTimeline={single}
            />
          ))}
          <Footer />
        </Page>
      )}
    </Document>
  )
}

export default SafetyIncidentLogPDF
